import { acceptHMRUpdate, defineStore } from 'pinia';
import type { Ref } from 'vue';
import { computed, ref } from 'vue';
import type {
  Address,
  FullAddress,
  Order,
  OrderVariant,
} from '@cutr/constants/order';
import type { SaveOptions } from '@/components/elements/Order/types';
import api from '@/helpers/api';
import { pick } from '@/helpers/helpers';
import { hasReplacedQuote, makeEmptyOrder } from '@/helpers/order-helpers';
import { isEmpty, isPlainObject } from '@/helpers/assertions';
import { track } from '@/helpers/tracker';
import { useActiveUserStore } from '@/stores/user';
import { quoteIsActive } from '@/helpers/quote-helpers';
import * as Sentry from '@sentry/vue';
import { modalManager } from '@/helpers/modal-manager';
import { isDraftOrder } from '@/helpers/orderStatusOptions';
import { useOrderUI } from '@/stores/orderUi';
import { bus, busEvents } from '@/composables/plugins';

export const DEFAULT_ORDER_SAVE_PROPS: (keyof Order)[] = [
  'name',
  'additionalInfo',
  'adminAdditionalInfo',
  'delivery',
  'customerFulfilmentAddressId',
  'customerFulfilmentAddress',
  'customerBillingAddressId',
  'customerBillingAddress',
  'makerFulfilment',
  'leftOverMaterialManagement',
  'requestedDeliveryDate',
  'supplierReference',
  'reference',
  'targetPriceAmount',
  'fscCertificationRequested',
  'isPositions',
];

const DEFAULT_DELIVERY_ADDRESS = {
  id: null,
  label: '',
  postal_code: '',
  country: 'NL',
  city: null,
} as FullAddress;

export const useOrderStore = defineStore('order', () => {
  const currentOrder: Ref<Order> = ref(makeEmptyOrder());
  const currentStep = ref<string>('project');
  const isLoading = ref(false);
  const isClosing = ref(false);
  const cutlistId = ref<string | null>(null);
  const addresses: Ref<FullAddress[]> = ref([]);
  const activeVariantId = ref<string | null>(null);
  // Tells a country the user submitted apart from DEFAULT_DELIVERY_ADDRESS.
  const deliveryAddressEdited = ref(false);
  const deliveryAddress: Ref<FullAddress> = ref({
    ...DEFAULT_DELIVERY_ADDRESS,
  });

  const userStore = useActiveUserStore();

  const isCustomer = computed(() => userStore.isOrderCustomer);
  const isDraft = computed(() => isDraftOrder(currentOrder.value?.orderstatus));

  const customerIsEditing = computed(() => isCustomer.value && isDraft.value);
  const isEditing = computed(
    () => userStore.isAdmin || customerIsEditing.value
  );

  const isCutlist = computed(() =>
    (currentOrder.value?.orderVariants || []).some((v) => v.cutlistId)
  );

  const currentVariant = computed(
    () =>
      currentOrder.value?.orderVariants?.find(
        (v) => v.id === activeVariantId.value
      ) as OrderVariant
  );

  // A variant without its own type yet is priced with the order's.
  const variantProductType = computed(
    () => currentVariant.value?.productType ?? currentOrder.value.productType
  );

  const orderId = computed(() => currentOrder.value.id);

  const defaultVariant = computed(() => {
    let latestMakerOffer = currentOrder.value?.makerOffers?.[0];
    for (const offer of currentOrder.value?.makerOffers || []) {
      if (
        offer.createdAt &&
        (!latestMakerOffer?.createdAt ||
          offer.createdAt > latestMakerOffer.createdAt)
      ) {
        latestMakerOffer = offer;
      }
    }

    const activeQuotes =
      currentOrder.value?.customerQuotes?.filter(quoteIsActive) || [];
    let latestQuote = activeQuotes[0];
    for (const quote of activeQuotes) {
      if (quote.createdAt > latestQuote?.createdAt) {
        latestQuote = quote;
      }
    }

    const variants = currentOrder.value?.orderVariants || [];
    let latestVariant = variants[0];
    for (const variant of variants) {
      if (variant.createdAt > latestVariant?.createdAt) {
        latestVariant = variant;
      }
    }

    return (
      latestMakerOffer?.orderVariantId ||
      latestQuote?.orderVariantId ||
      latestVariant?.id
    );
  });

  const setOrder = (order: Order) => {
    currentOrder.value = order;
  };

  const setLoading = (loading: boolean) => {
    isLoading.value = loading;
  };

  function reset() {
    currentOrder.value = makeEmptyOrder();
    activeVariantId.value = null;
    deliveryAddress.value = { ...DEFAULT_DELIVERY_ADDRESS };
    deliveryAddressEdited.value = false;
    addresses.value = [];
    isClosing.value = false;
    cutlistId.value = null;
  }

  async function reloadDataForCurrentOrder(opts: SaveOptions = {}) {
    if (!currentOrder.value) return;
    const orderId = currentOrder.value.id;
    const { data } = await api.get(`/v2/orders/${orderId}`);
    // Avoid overwriting a newly selected order's data with the response for the previous one.
    if (currentOrder.value?.id !== orderId) return;
    applyDataToCurrentOrder(data.data, opts);
  }

  function applyDataToCurrentOrder(
    data: Partial<Order>,
    opts: SaveOptions = {}
  ) {
    if (!currentOrder.value) return;

    const d: Partial<Order> = opts.include ? pick(data, opts.include) : data;

    Object.keys(d).forEach((k) => {
      const key = k as keyof Order;
      // @ts-expect-error TODO: fix
      currentOrder.value[key] = d[key];
    });

    currentOrder.value.reloadedAt = Date.now();

    if (opts.reason === 'Customer accepted quote.') {
      if (hasReplacedQuote(currentOrder.value)) {
        modalManager.open('global', {
          header: 'New quote accepted.',
          body: 'Future invoices will reflect this new quote.',
          button2label: 'Ok.',
          button2signal: true,
        });
        return;
      }
    }
  }

  function applyDataToCurrentOrderAndSave(
    data: Partial<Order>,
    options?: SaveOptions
  ) {
    applyDataToCurrentOrder(data, options);
    return saveOrder(options);
  }

  function serialiseOrder(order: Order, options: SaveOptions = {}) {
    if (!currentOrder.value) return;

    const saveProps = [...DEFAULT_ORDER_SAVE_PROPS];
    if (currentOrder.value.targetPriceType) {
      saveProps.push('targetPriceType');
    }

    const result: Partial<Order> = {};

    for (const prop of options.include || saveProps) {
      if (prop in order) {
        const value = order[prop as keyof Order];
        let transformed = value;

        if (isPlainObject(value) && isEmpty(value)) {
          transformed = null;
        }

        if (transformed === '') transformed = null;

        result[prop as keyof Order] = transformed;
      }
    }

    return result;
  }

  async function saveOrder(options: SaveOptions = {}) {
    if (!currentOrder.value) return;

    try {
      await saveOrderVariantData();
      await saveDeliveryAddress();

      const { data } = await api.post(`/v2/orders/${currentOrder.value.id}`, {
        data: serialiseOrder(currentOrder.value, options),
        reason: options.reason,
      });

      if (isCustomer.value && options.reason === 'auto-save') {
        track('order:intake-save-and-close', {
          isDraft: isDraft.value,
          order: currentOrder.value,
        });
      }

      if (isClosing.value) return;

      applyDataToCurrentOrder(data.data, options);

      useOrderUI().lastSavedAt = new Date();
      bus.$emit(busEvents.orderUpdated, options, data.data);
    } catch (err) {
      console.error(err);
      track('order:error', {
        event: options?.reason,
        err,
        order: currentOrder.value,
      });
      bus.$emit(busEvents.orderUpdateError, err);
      Sentry.captureException(err);
      throw err;
    }
  }

  async function populateAddresses() {
    if (userStore.isMaker) return;
    if (userStore.isAdmin && !currentOrder.value?.customerOrganisationId) {
      addresses.value = [];
      return;
    }

    let query = '';
    if (userStore.isAdmin) {
      query = `?customerOrganisationId=${currentOrder.value?.customerOrganisationId}`;
    }

    return api.get(`/v2/addresses${query}`).then(({ data }) => {
      addresses.value = data.data;

      if (!currentOrder.value) return;

      if (currentOrder.value.customerFulfilmentAddressId) {
        const deliveryAddress = addresses.value.find(
          (a) => a.id === currentOrder.value!.customerFulfilmentAddressId
        );
        if (deliveryAddress) {
          updateDeliveryAddress(deliveryAddress);
        }
      }
      if (!deliveryAddress.value?.id && addresses.value.length === 1) {
        updateDeliveryAddress(addresses.value[0]);
      }
      if (!currentOrder.value.customerBillingAddressId) {
        const billingAddress = addresses.value.find((a) => a.isDefaultBilling);
        if (billingAddress) {
          updateBillingAddress(billingAddress);
        }
      }
    });
  }

  function setAddress(address: FullAddress, type: 'billing' | 'fulfilment') {
    if (type === 'billing') return setBillingAddress(address);
    if (type === 'fulfilment') return setFulfilmentAddress(address);
  }

  function setBillingAddress(address: Pick<Address, 'id'>) {
    return applyDataToCurrentOrderAndSave(
      { customerBillingAddressId: address.id },
      {
        include: ['customerBillingAddressId', 'customerBillingAddress'],
        reason: `${userStore.activeSessionType} update billing address`,
      }
    );
  }

  function setFulfilmentAddress(address: Pick<Address, 'id'>) {
    return applyDataToCurrentOrderAndSave(
      { customerFulfilmentAddressId: address.id },
      {
        include: [
          'customerFulfilmentAddressId',
          'customerFulfilmentAddress',
          'makerFulfilmentAddress',
        ],
        reason: `${userStore.activeSessionType} update delivery address`,
      }
    );
  }

  function replaceLinkedAddress(address: FullAddress) {
    if (currentOrder.value.customerFulfilmentAddressId === address.id) {
      currentOrder.value.customerFulfilmentAddress = address;
    }
    if (currentOrder.value.customerBillingAddressId === address.id) {
      currentOrder.value.customerBillingAddress = address;
    }
    return populateAddresses();
  }

  function updateDeliveryAddress(data: FullAddress) {
    if (!currentOrder.value) return;
    if (!deliveryAddress.value) return;

    deliveryAddress.value.id = data.id;
    deliveryAddress.value.line1 = data.line1 || null;
    deliveryAddress.value.line2 = data.line2;
    deliveryAddress.value.postal_code = data.postal_code;
    deliveryAddress.value.country = data.country;
    deliveryAddress.value.label = data.label;
    deliveryAddress.value.city = data.city;
    currentOrder.value.customerFulfilmentAddressId = data.id;
  }

  function updateBillingAddress(data: Order['customerBillingAddress']) {
    if (!currentOrder.value) return;

    currentOrder.value.customerBillingAddress = data;
    currentOrder.value.customerBillingAddressId = data.id;
  }

  async function saveDeliveryAddress() {
    if (currentStep.value !== 'delivery') return;
    if (!isCustomer.value && !userStore.isAdmin) return;
    if (addresses.value.length > 1) return;
    if (!deliveryAddress.value) return;
    if (deliveryAddress.value.label) return;

    const customerOrganisationId = currentOrder.value?.customerOrganisationId;
    if (userStore.isAdmin) {
      if (!customerOrganisationId) return;
      if (!deliveryAddress.value.id && !deliveryAddressEdited.value) return;
    }

    const { line1, line2, city, postal_code, country } = deliveryAddress.value;
    return api
      .post(
        `/v2/addresses/${deliveryAddress.value.id || ''}?minimal=true`,
        { line1, line2, city, postal_code, country },
        {
          params: userStore.isAdmin ? { customerOrganisationId } : undefined,
        }
      )
      .then(() => populateAddresses());
  }

  async function saveOrderVariantData() {
    if (!isEditing.value) return;
    if (!currentVariant.value || !activeVariantId.value) return;

    const data = {} as Record<string, unknown>;

    if (currentStep.value === 'materials') {
      data.hardwareNotes = currentVariant.value.hardwareNotes;
    }

    if (!Object.keys(data).length) return;

    return api.post(`/v2/order-variants/${activeVariantId.value}`, data);
  }

  function linkCutlistToCurrentVariant(id: string) {
    if (!isCustomer.value) return;

    cutlistId.value = id;
  }

  return {
    currentOrder,
    isLoading,
    isClosing,
    setOrder,
    setLoading,
    setAddress,
    saveDeliveryAddress,
    saveOrderVariantData,
    defaultVariant,
    activeVariantId,
    currentVariant,
    variantProductType,
    orderId,
    deliveryAddress,
    deliveryAddressEdited,
    isCutlist,
    addresses,
    currentStep,
    reloadDataForCurrentOrder,
    applyDataToCurrentOrder,
    applyDataToCurrentOrderAndSave,
    populateAddresses,
    replaceLinkedAddress,
    saveOrder,
    reset,
    isEditing,
    customerIsEditing,
    isDraft,
    isCustomer,
    cutlistId,
    linkCutlistToCurrentVariant,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useOrderStore, import.meta.hot));
}
